import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { motion } from 'framer-motion';

const ContactDemo = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Demo: mostrar que el formulario fue enviado
    alert('¡Mensaje enviado! En la versión completa se procesaría el formulario.');
    setFormData({ name: '', email: '', message: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section 
      id="contacto" 
      className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-ocean-900 text-white relative overflow-hidden"
    >
      {/* Simplified background effects */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-48 h-48 bg-jade-400 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-32 right-16 w-32 h-32 bg-ocean-500 rounded-full filter blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 relative">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 font-crimson antialiased">
            Contacto
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto font-dmsans antialiased">
            ¡Estamos aquí para ayudarte! Consulta disponibilidad y precios
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Contact Info */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-semibold mb-6 text-jade-300 font-crimson antialiased">
                Información de Contacto
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-ocean-500/20 p-3 rounded-full">
                    <Phone className="w-6 h-6 text-ocean-300" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white font-dmsans">Teléfono</h4>
                    <p className="text-slate-300 font-dmsans">+54 9 11 XXXX-XXXX</p>
                    <p className="text-sm text-slate-400 font-dmsans">WhatsApp disponible</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-jade-500/20 p-3 rounded-full">
                    <Mail className="w-6 h-6 text-jade-300" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white font-dmsans">Email</h4>
                    <p className="text-slate-300 font-dmsans">contacto@mardejade.com</p>
                    <p className="text-sm text-slate-400 font-dmsans">Respuesta en 24hs</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-seagreen-500/20 p-3 rounded-full">
                    <MapPin className="w-6 h-6 text-seagreen-300" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white font-dmsans">Ubicación</h4>
                    <p className="text-slate-300 font-dmsans">Buenos Aires, Argentina</p>
                    <p className="text-sm text-slate-400 font-dmsans">Delivery disponible</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Info */}
            <div className="bg-gradient-to-r from-ocean-600/20 to-jade-600/20 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h4 className="text-lg font-medium text-white mb-2 font-crimson">
                ¿Primera vez comprando mariscos?
              </h4>
              <p className="text-slate-300 text-sm font-dmsans">
                Te asesoramos sobre los mejores productos para tu ocasión especial
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-semibold mb-6 text-jade-300 font-crimson antialiased">
                Envíanos un Mensaje
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2 font-dmsans">
                    Nombre
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent text-white placeholder-slate-400 font-dmsans"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2 font-dmsans">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent text-white placeholder-slate-400 font-dmsans"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2 font-dmsans">
                    Mensaje
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent text-white placeholder-slate-400 resize-none font-dmsans"
                    placeholder="Cuéntanos qué necesitas, fechas de entrega, cantidad de personas..."
                  />
                </div>

                <motion.button
                  type="submit"
                  className="w-full bg-gradient-to-r from-ocean-500 to-jade-500 hover:from-ocean-600 hover:to-jade-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 font-dmsans"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Send className="w-5 h-5" />
                  Enviar Mensaje
                </motion.button>
              </form>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default ContactDemo;
